import { computed, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useIntervalFn, useEventListener } from '@vueuse/core';
import { useAccount } from './useAccount';
import { useStore } from './store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export function useActivationGuide() {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const { accountId, currentAccount } = useAccount();
  const guide = computed(() =>
    store.getters['activationGuide/forAccount'](accountId.value)
  );
  const enabled = computed(() => {
    const member = store.getters.getCurrentUser.accounts?.find(
      account => account.id === accountId.value
    );
    return (
      currentAccount.value?.features?.activation_guide &&
      member?.role === 'administrator'
    );
  });
  const selected = computed(() => route.name === 'activation_guide');

  const refresh = async () => {
    if (!enabled.value) {
      if (selected.value && currentAccount.value) {
        router.replace({
          name: 'home',
          params: { accountId: accountId.value },
        });
      }
      return;
    }
    const requestedAccount = accountId.value;
    const data = await store.dispatch(
      'activationGuide/fetch',
      requestedAccount
    );
    if (requestedAccount !== accountId.value) return;
    if (
      data?.eligible &&
      route.name === 'home' &&
      !store.getters['activationGuide/wasAutoOpened'](requestedAccount)
    ) {
      store.commit('activationGuide/autoOpened', requestedAccount);
      router.replace({
        name: 'activation_guide',
        params: { accountId: requestedAccount },
      });
    }
  };

  watch([enabled, accountId], refresh, { immediate: true });
  watch(
    selected,
    value => {
      if (value) store.commit('activationGuide/autoOpened', accountId.value);
    },
    { immediate: true }
  );
  useIntervalFn(() => {
    if (guide.value.eligible && document.visibilityState === 'visible')
      refresh();
  }, 15000);
  useEventListener(document, 'visibilitychange', refresh);
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, refresh);
  onBeforeUnmount(() => emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, refresh));

  return { guide, guideEnabled: enabled, guideSelected: selected };
}
